import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PlacesAutocomplete from "../../components/form/input/PlacesAutocomplete";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Select from "../../components/form/Select";
import { SelectOption } from "../../types/select";
import {
  formatFormNumber,
  normalizeBookingForForm,
} from "../../utils/normalizeBookingForm";
import {
  syncPickupDatetimeMirrorFields,
  ukDatetimeLocalToIso,
} from "../../utils/dateUtils";
import {
  postalCodeFromPlace,
  type PlacesLocation,
} from "../../utils/placeAutocomplete";
import {
  mapAuditProgressToBookingForm,
  type AuditCreateBookingState,
} from "../../utils/mapAuditProgressToBookingForm";

type ViaSlot = 1 | 2 | 3;
type ViaPosition = "pre" | "post";

const VIA_SLOTS: ViaSlot[] = [1, 2, 3];
const MAX_VIAS = 3;

function countFilledVias(...addresses: string[]): number {
  let count = 0;
  addresses.forEach((address, index) => {
    if (String(address ?? "").trim()) count = index + 1;
  });
  return count;
}

interface BookingFormData {
  id?: string;
  bookingId: number;
  addDedLeg: string;
  formType: string;
  jobNo: string;
  waitTime: string;
  vehicleNo: string;
  vehicleMake: string;
  vehicleModel: string;
  paymentMode: string;
  driverId: string;
  cabTypeId: string;
  dedLegBookingId: string;
  fromBookingId: string;
  pickupDateTime: string;
  pickupDateTimeStr: string;
  pickupTime: string;
  pickupTimeSpan: string;
  pickupDateWithTime: string;
  pickupPostalCode: string;
  pickupPostalCode1: string;
  pickupPostalCode2: string;
  pickupPostalCode3: string;
  pickupLongitude: string;
  pickupLongitude1: string;
  pickupLongitude2: string;
  pickupLongitude3: string;
  pickupLatitude: string;
  pickupLatitude1: string;
  pickupLatitude2: string;
  pickupLatitude3: string;
  dropoffDateTime: string;
  dropoffPostalCode: string;
  dropoffPostalCode1: string;
  dropoffPostalCode2: string;
  dropoffPostalCode3: string;
  dropoffLongitude: string;
  dropoffLongitude1: string;
  dropoffLongitude2: string;
  dropoffLongitude3: string;
  dropoffLatitude: string;
  dropoffLatitude1: string;
  dropoffLatitude2: string;
  dropoffLatitude3: string;
  payment: string;
  pasengerName: string;
  cellno: string;
  email: string;
  vehicleTypeId: string;
  vehicleTypeName: string;
  amount: number;
  miles: number;
  status: string;
  saveDateTime: string;
  paybalEmail: string;
  cardFirstName: string;
  creditCardNo: string;
  creditCvvCode: number;
  creditMonth: number;
  creditYear: number;
  startDateTime: string;
  closeDateTime: string;
  startingMeterMiles: string;
  closingMeterMiles: string;
  totalMeterMiles: number;
  luggageDetail: string;
  noofPassenger: number;
  luggagekg: number;
  childSeat: number;
  disableSeat: number;
  returnTrip: number;
  meetGreet: number;
  termsCheck: number;
  noOfAdult: number;
  noOfChild: number;
  arrivalFrom: string;
  flightNo: string;
  flightTime: string;
  terminal: string;
  doorNo: string;
  additionalInfo: string;
  calculatedfare: number;
  estimateFareNet: number;
  pickupTimeString: string;
  flightTimeString: string;
  startpoint: string;
  startpoint1: string;
  startpoint2: string;
  startpoint3: string;
  endposition: string;
  endposition1: string;
  endposition2: string;
  endposition3: string;
  paymentNo: string;
  surName: string;
  meetGreetStr: string;
  confirmStatus: string;
  imageName: string;
  detailCode: number;
  name: string;
  driverArrivedDateTimeStr: string;
  pobDateTimeStr: string;
  doneDateTimeStr: string;
  manualPickupAddress: string;
  manualDropOffAddress: string;
  promotionId: number;
  pickupTimeT: string;
  flightTimeT: string;
  cancelRemarks: string;
  bookingSource: string;
  paymentStatus: string;
  expireStatus: string;
  showDate: number;
  fromDateTime: string;
  toDateTime: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  bookDateTime: string;
  cabId: string;
  cancelDateTime: string;
  childSeatStr: string;
  createDateTime: string;
  detailName: string;
  disableSeatStr: string;
  doneDateTime: string;
  driverArrivedDateTime: string;
  logStatus: string;
  luggageDesc: string;
  luggageId: number;
  pobDateTime: string;
  recordId: number;
  returnDate: string;
  returnTime: string;
  returnTimeStr: string;
  returnTripStr: string;
  viewDateTime: string;
  workSection: string;
}

interface VehicleType {
  id: string;
  name?: string | null;
  description: string;
}

interface AssignableDriver {
  id: string;
  fullName: string;
  mobileNumber: string;
  isAvailable: boolean;
}

function viaAddressKey(position: ViaPosition, slot: ViaSlot): keyof BookingFormData {
  return (position === "pre" ? `startpoint${slot}` : `endposition${slot}`) as keyof BookingFormData;
}

function viaPostalKey(position: ViaPosition, slot: ViaSlot): keyof BookingFormData {
  return (
    position === "pre" ? `pickupPostalCode${slot}` : `dropoffPostalCode${slot}`
  ) as keyof BookingFormData;
}

function viaLatKey(position: ViaPosition, slot: ViaSlot): keyof BookingFormData {
  return (
    position === "pre" ? `pickupLatitude${slot}` : `dropoffLatitude${slot}`
  ) as keyof BookingFormData;
}

function viaLngKey(position: ViaPosition, slot: ViaSlot): keyof BookingFormData {
  return (
    position === "pre" ? `pickupLongitude${slot}` : `dropoffLongitude${slot}`
  ) as keyof BookingFormData;
}

function clearViaSlotFields(
  data: BookingFormData,
  position: ViaPosition,
  slot: ViaSlot,
): BookingFormData {
  return {
    ...data,
    [viaAddressKey(position, slot)]: "",
    [viaPostalKey(position, slot)]: "",
    [viaLatKey(position, slot)]: "",
    [viaLngKey(position, slot)]: "",
  };
}

function compactViaSlots(
  data: BookingFormData,
  position: ViaPosition,
): BookingFormData {
  const kept = VIA_SLOTS.map((slot) => ({
    address: String(data[viaAddressKey(position, slot)] ?? ""),
    postal: String(data[viaPostalKey(position, slot)] ?? ""),
    lat: String(data[viaLatKey(position, slot)] ?? ""),
    lng: String(data[viaLngKey(position, slot)] ?? ""),
  })).filter((via) => via.address.trim() || via.postal.trim());

  let next = { ...data };
  VIA_SLOTS.forEach((slot) => {
    next = clearViaSlotFields(next, position, slot);
  });
  kept.forEach((via, index) => {
    const slot = (index + 1) as ViaSlot;
    next = {
      ...next,
      [viaAddressKey(position, slot)]: via.address,
      [viaPostalKey(position, slot)]: via.postal,
      [viaLatKey(position, slot)]: via.lat,
      [viaLngKey(position, slot)]: via.lng,
    };
  });
  return next;
}

export default function BookingEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const auditPrefillApplied = useRef(false);

  const [formData, setFormData] = useState<BookingFormData>({
    bookingId: 0,
    addDedLeg: "",
    formType: "",
    jobNo: "",
    waitTime: "",
    vehicleNo: "",
    vehicleMake: "",
    vehicleModel: "",
    paymentMode: "cash",
    driverId: "",
    cabTypeId: "",
    dedLegBookingId: "",
    fromBookingId: "",
    pickupDateTime: "",
    pickupDateTimeStr: "",
    pickupTime: "",
    pickupTimeSpan: "",
    pickupDateWithTime: "",
    pickupPostalCode: "",
    pickupPostalCode1: "",
    pickupPostalCode2: "",
    pickupPostalCode3: "",
    pickupLongitude: "",
    pickupLongitude1: "",
    pickupLongitude2: "",
    pickupLongitude3: "",
    pickupLatitude: "",
    pickupLatitude1: "",
    pickupLatitude2: "",
    pickupLatitude3: "",
    dropoffDateTime: "",
    dropoffPostalCode: "",
    dropoffPostalCode1: "",
    dropoffPostalCode2: "",
    dropoffPostalCode3: "",
    dropoffLongitude: "",
    dropoffLongitude1: "",
    dropoffLongitude2: "",
    dropoffLongitude3: "",
    dropoffLatitude: "",
    dropoffLatitude1: "",
    dropoffLatitude2: "",
    dropoffLatitude3: "",
    payment: "",
    pasengerName: "",
    cellno: "",
    email: "",
    vehicleTypeId: "",
    vehicleTypeName: "",
    amount: 0,
    miles: 0,
    status: "pending",
    saveDateTime: "",
    paybalEmail: "",
    cardFirstName: "",
    creditCardNo: "",
    creditCvvCode: 0,
    creditMonth: 0,
    creditYear: 0,
    startDateTime: "",
    closeDateTime: "",
    startingMeterMiles: "",
    closingMeterMiles: "",
    totalMeterMiles: 0,
    luggageDetail: "",
    noofPassenger: 1,
    luggagekg: 0,
    childSeat: 0,
    disableSeat: 0,
    returnTrip: 0,
    meetGreet: 0,
    termsCheck: 0,
    noOfAdult: 1,
    noOfChild: 0,
    arrivalFrom: "",
    flightNo: "",
    flightTime: "",
    terminal: "",
    doorNo: "",
    additionalInfo: "",
    calculatedfare: 0,
    estimateFareNet: 0,
    pickupTimeString: "",
    flightTimeString: "",
    startpoint: "",
    startpoint1: "",
    startpoint2: "",
    startpoint3: "",
    endposition: "",
    endposition1: "",
    endposition2: "",
    endposition3: "",
    paymentNo: "",
    surName: "",
    meetGreetStr: "",
    confirmStatus: "",
    imageName: "",
    detailCode: 0,
    name: "",
    driverArrivedDateTimeStr: "",
    pobDateTimeStr: "",
    doneDateTimeStr: "",
    manualPickupAddress: "",
    manualDropOffAddress: "",
    promotionId: 0,
    pickupTimeT: "",
    flightTimeT: "",
    cancelRemarks: "",
    bookingSource: "dashboard",
    paymentStatus: "pending",
    expireStatus: "",
    showDate: 0,
    fromDateTime: "",
    toDateTime: "",
    createdBy: "",
    createdAt: "",
    updatedBy: "",
    updatedAt: "",
    bookDateTime: "",
    cabId: "",
    cancelDateTime: "",
    childSeatStr: "",
    createDateTime: "",
    detailName: "",
    disableSeatStr: "",
    doneDateTime: "",
    driverArrivedDateTime: "",
    logStatus: "",
    luggageDesc: "",
    luggageId: 0,
    pobDateTime: "",
    recordId: 0,
    returnDate: "",
    returnTime: "",
    returnTimeStr: "",
    returnTripStr: "",
    viewDateTime: "",
    workSection: ""
  });

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [drivers, setDrivers] = useState<AssignableDriver[]>([]);
  const [assignedDriverName, setAssignedDriverName] = useState("");
  const [assignedDriverPhone, setAssignedDriverPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMessage, setAssignMessage] = useState("");
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [error, setError] = useState("");
  const [preViaCount, setPreViaCount] = useState(0);
  const [postViaCount, setPostViaCount] = useState(0);

  const syncViaCountsFromBooking = (data: Record<string, unknown> | BookingFormData) => {
    setPreViaCount(
      countFilledVias(
        String(data.startpoint1 ?? ""),
        String(data.startpoint2 ?? ""),
        String(data.startpoint3 ?? ""),
      ),
    );
    setPostViaCount(
      countFilledVias(
        String(data.endposition1 ?? ""),
        String(data.endposition2 ?? ""),
        String(data.endposition3 ?? ""),
      ),
    );
  };

  const fetchBookingData = useCallback(async () => {
    if (!id) return;
    setPageLoading(true);
    try {
      const response = await axiosInstance.get(`/direct-bookings/${id}`);
      const data = response.data as Record<string, unknown>;
      setFormData((prev) => normalizeBookingForForm(data, prev));
      syncViaCountsFromBooking(data);
      const driver = data.driver as { fullName?: string; mobileNumber?: string } | undefined;
      setAssignedDriverName(driver?.fullName ?? "");
      setAssignedDriverPhone(driver?.mobileNumber ?? "");
    } catch (error) {
      console.error("Error fetching booking:", error);
      setError("Failed to fetch booking data");
    } finally {
      setPageLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVehicleTypes();
    fetchAvailableDrivers();
    if (isEditMode) {
      fetchBookingData();
    }
  }, [isEditMode, fetchBookingData]);

  useEffect(() => {
    if (isEditMode || auditPrefillApplied.current) return;
    const state = location.state as AuditCreateBookingState | null;
    if (!state?.auditPrefill || typeof state.auditPrefill !== "object") return;

    auditPrefillApplied.current = true;
    const patch = mapAuditProgressToBookingForm(
      state.auditPrefill,
      state.bookingSource,
    );
    setFormData((prev) => {
      const next = { ...prev, ...patch };
      syncViaCountsFromBooking(next);
      return next;
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [isEditMode, location.state, location.pathname, navigate]);

  const fetchAvailableDrivers = async () => {
    try {
      const response = await axiosInstance.get<AssignableDriver[]>("/drivers/available");
      setDrivers(response.data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await axiosInstance.get<VehicleType[]>("/vehicletypes");
      setVehicleTypes(response.data);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      setError("Failed to fetch vehicle types");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? ((e.target as HTMLInputElement).checked ? 1 : 0) :
              type === 'number' ? Number(value) :
              value
    }));
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyPlaceToFields = (
    address: string,
    location: PlacesLocation,
    components: google.maps.GeocoderAddressComponent[],
    addressKey: keyof BookingFormData,
    postalKey: keyof BookingFormData,
    latKey: keyof BookingFormData,
    lngKey: keyof BookingFormData,
  ) => {
    const postalCode = postalCodeFromPlace(address, components);
    setFormData((prev) => ({
      ...prev,
      [addressKey]: address,
      [postalKey]: postalCode || prev[postalKey],
      [latKey]: location.lat ? String(location.lat) : prev[latKey],
      [lngKey]: location.lng ? String(location.lng) : prev[lngKey],
    }));
  };

  const handlePickupPlaceSelect = (
    address: string,
    _placeId: string,
    location: PlacesLocation,
    components: google.maps.GeocoderAddressComponent[],
  ) => {
    applyPlaceToFields(
      address,
      location,
      components,
      "manualPickupAddress",
      "pickupPostalCode",
      "pickupLatitude",
      "pickupLongitude",
    );
  };

  const handleDropoffPlaceSelect = (
    address: string,
    _placeId: string,
    location: PlacesLocation,
    components: google.maps.GeocoderAddressComponent[],
  ) => {
    applyPlaceToFields(
      address,
      location,
      components,
      "manualDropOffAddress",
      "dropoffPostalCode",
      "dropoffLatitude",
      "dropoffLongitude",
    );
  };

  const handleViaPlaceSelect =
    (position: ViaPosition, slot: ViaSlot) =>
    (
      address: string,
      _placeId: string,
      location: PlacesLocation,
      components: google.maps.GeocoderAddressComponent[],
    ) => {
      applyPlaceToFields(
        address,
        location,
        components,
        viaAddressKey(position, slot),
        viaPostalKey(position, slot),
        viaLatKey(position, slot),
        viaLngKey(position, slot),
      );
    };

  const handleAddVia = (position: ViaPosition) => {
    if (position === "pre") {
      setPreViaCount((count) => Math.min(MAX_VIAS, count + 1));
      return;
    }
    setPostViaCount((count) => Math.min(MAX_VIAS, count + 1));
  };

  const handleRemoveVia = (position: ViaPosition, slot: ViaSlot) => {
    setFormData((prev) => {
      const cleared = clearViaSlotFields(prev, position, slot);
      return compactViaSlots(cleared, position);
    });
    if (position === "pre") {
      setPreViaCount((count) => Math.max(0, count - 1));
      return;
    }
    setPostViaCount((count) => Math.max(0, count - 1));
  };

  const handleAssignDriver = async () => {
    if (!id || !formData.driverId) {
      setError("Select a driver before assigning");
      return;
    }

    setAssignLoading(true);
    setAssignMessage("");
    setError("");

    try {
      const response = await axiosInstance.post(`/direct-bookings/${id}/assign-driver`, {
        driverId: formData.driverId,
      });
      const data = response.data as Record<string, unknown>;
      setFormData((prev) => normalizeBookingForForm(data, prev));
      syncViaCountsFromBooking(data);
      const driver = data.driver as { fullName?: string; mobileNumber?: string } | undefined;
      setAssignedDriverName(driver?.fullName ?? "");
      setAssignedDriverPhone(driver?.mobileNumber ?? "");
      setAssignMessage("Driver assigned. Journey details email sent to the customer.");
    } catch (error: unknown) {
      console.error("Error assigning driver:", error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "Failed to assign driver");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Create a copy of formData and remove the id field for new bookings
      const dataToSubmit = { ...formData };
      if (!isEditMode) {
        delete dataToSubmit.id;
      }

      // Format dates to ISO string (UK local time from datetime-local inputs)
      if (dataToSubmit.pickupDateTime) {
        const pickupIso = ukDatetimeLocalToIso(dataToSubmit.pickupDateTime);
        dataToSubmit.pickupDateTime = pickupIso;
        Object.assign(dataToSubmit, syncPickupDatetimeMirrorFields(pickupIso));
      }
      if (dataToSubmit.dropoffDateTime) {
        dataToSubmit.dropoffDateTime = ukDatetimeLocalToIso(dataToSubmit.dropoffDateTime);
      }
      if (dataToSubmit.flightTime) {
        dataToSubmit.flightTime = ukDatetimeLocalToIso(dataToSubmit.flightTime);
      }

      // Keep website booking address fields in sync with manual edit fields.
      dataToSubmit.startpoint = dataToSubmit.manualPickupAddress || dataToSubmit.startpoint;
      dataToSubmit.endposition = dataToSubmit.manualDropOffAddress || dataToSubmit.endposition;

      // Validate required fields
      const requiredFields = [
        'pasengerName',
        'cellno',
        'email',
        'vehicleTypeId',
        'pickupDateTime',
        'pickupPostalCode',
        'dropoffPostalCode',
        'manualPickupAddress',
        'manualDropOffAddress'
      ];

      const missingFields = requiredFields.filter(field => !dataToSubmit[field as keyof typeof dataToSubmit]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      dataToSubmit.bookingSource = dataToSubmit.bookingSource || "dashboard";

      dataToSubmit.noofPassenger =
        (Number(dataToSubmit.noOfAdult) || 0) + (Number(dataToSubmit.noOfChild) || 0);

      if (isEditMode) {
        await axiosInstance.put(`/direct-bookings/${id}`, dataToSubmit);
      } else {
        const response = await axiosInstance.post("/direct-bookings", dataToSubmit);
        console.log('Response:', response.data);
      }
      navigate("/bookings");
    } catch (error: any) {
      console.error("Error saving booking:", error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
        setError(error.response.data?.message || error.response.data?.title || 'Failed to save booking');
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        setError('No response received from server');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
        setError(error.message || 'Failed to save booking');
      }
    } finally {
      setLoading(false);
    }
  };

  const vehicleTypeOptions: SelectOption[] = useMemo(() => {
    const options = vehicleTypes.map((type) => ({
      value: type.id,
      label: type.name?.trim() || type.description,
    }));

    if (
      formData.vehicleTypeId &&
      !options.some((option) => option.value === formData.vehicleTypeId)
    ) {
      options.unshift({
        value: formData.vehicleTypeId,
        label: formData.vehicleTypeName || "Current vehicle type",
      });
    }

    return options;
  }, [formData.vehicleTypeId, formData.vehicleTypeName, vehicleTypes]);

  const paymentModeOptions: SelectOption[] = [
    { value: "cash", label: "Cash" },
    { value: "card", label: "Card" },
    { value: "online", label: "Online" },
    { value: "invoice", label: "Invoice (email)" },
  ];

  const statusOptions: SelectOption[] = [
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Confirmed" },
    { value: "assigned", label: "Assigned" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "Paid", label: "Paid" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const driverOptions: SelectOption[] = useMemo(() => {
    const options = drivers.map((driver) => ({
      value: driver.id,
      label: `${driver.fullName} (${driver.mobileNumber})`,
    }));

    if (
      formData.driverId &&
      !options.some((option) => option.value === formData.driverId)
    ) {
      options.unshift({
        value: formData.driverId,
        label: assignedDriverName
          ? `${assignedDriverName}${assignedDriverPhone ? ` (${assignedDriverPhone})` : ""}`
          : "Currently assigned driver",
      });
    }

    return options;
  }, [assignedDriverName, assignedDriverPhone, drivers, formData.driverId]);

  const paymentStatusOptions: SelectOption[] = [
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "paid", label: "Paid" },
    { value: "failed", label: "Failed" },
    { value: "refunded", label: "Refunded" },
  ];

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Booking" : "Add Booking"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Passenger Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Passenger Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputWithLabel
                  label="Passenger Name"
                  name="pasengerName"
                  value={formData.pasengerName}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Phone Number"
                  name="cellno"
                  value={formData.cellno}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Number of Adults"
                  type="number"
                  name="noOfAdult"
                  value={formatFormNumber(formData.noOfAdult)}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Number of Children"
                  type="number"
                  name="noOfChild"
                  value={formatFormNumber(formData.noOfChild)}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Luggage Weight (kg)"
                  type="number"
                  name="luggagekg"
                  value={formatFormNumber(formData.luggagekg)}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Vehicle and Journey Details */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle and Journey Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Vehicle Type"
                  name="vehicleTypeId"
                  value={formData.vehicleTypeId}
                  onChange={handleSelectChange("vehicleTypeId")}
                  options={vehicleTypeOptions}
                  required
                />
                <InputWithLabel
                  label="Pickup Date & Time"
                  type="datetime-local"
                  name="pickupDateTime"
                  value={formData.pickupDateTime}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Via stops before pickup */}
              {preViaCount > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Via stops before pickup</p>
                  {VIA_SLOTS.slice(0, preViaCount).map((slot) => (
                    <div
                      key={`pre-via-${slot}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end"
                    >
                      <PlacesAutocomplete
                        label={`Via ${slot} Address`}
                        type={`pre-via-${slot}`}
                        value={String(formData[viaAddressKey("pre", slot)] ?? "")}
                        onChange={handleViaPlaceSelect("pre", slot)}
                        placeholder="Start typing an address..."
                      />
                      <InputWithLabel
                        label={`Via ${slot} Postal Code`}
                        name={viaPostalKey("pre", slot)}
                        value={String(formData[viaPostalKey("pre", slot)] ?? "")}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveVia("pre", slot)}
                        className="mb-1 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <PlacesAutocomplete
                  label="Pickup Address"
                  type="pickup"
                  value={formData.manualPickupAddress}
                  onChange={handlePickupPlaceSelect}
                  placeholder="Start typing an address..."
                  required
                />
                <InputWithLabel
                  label="Pickup Postal Code"
                  name="pickupPostalCode"
                  value={formData.pickupPostalCode}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {preViaCount < MAX_VIAS && (
                <button
                  type="button"
                  onClick={() => handleAddVia("pre")}
                  className="mt-3 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  + Add via before pickup
                </button>
              )}

              {/* Via stops en route (after pickup, before dropoff) */}
              {postViaCount > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Via stops en route</p>
                  {VIA_SLOTS.slice(0, postViaCount).map((slot) => (
                    <div
                      key={`post-via-${slot}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end"
                    >
                      <PlacesAutocomplete
                        label={`Via ${slot} Address`}
                        type={`post-via-${slot}`}
                        value={String(formData[viaAddressKey("post", slot)] ?? "")}
                        onChange={handleViaPlaceSelect("post", slot)}
                        placeholder="Start typing an address..."
                      />
                      <InputWithLabel
                        label={`Via ${slot} Postal Code`}
                        name={viaPostalKey("post", slot)}
                        value={String(formData[viaPostalKey("post", slot)] ?? "")}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveVia("post", slot)}
                        className="mb-1 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {postViaCount < MAX_VIAS && (
                <button
                  type="button"
                  onClick={() => handleAddVia("post")}
                  className="mt-3 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  + Add via en route
                </button>
              )}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <PlacesAutocomplete
                  label="Dropoff Address"
                  type="dropoff"
                  value={formData.manualDropOffAddress}
                  onChange={handleDropoffPlaceSelect}
                  placeholder="Start typing an address..."
                  required
                />
                <InputWithLabel
                  label="Dropoff Postal Code"
                  name="dropoffPostalCode"
                  value={formData.dropoffPostalCode}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Wait Time"
                  name="waitTime"
                  value={formData.waitTime}
                  onChange={handleInputChange}
                  placeholder="e.g. Wait 15 mins at via 1"
                />
              </div>
            </div>

            {/* Flight Details */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Flight Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputWithLabel
                  label="Flight Number"
                  name="flightNo"
                  value={formData.flightNo}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Arrival From"
                  name="arrivalFrom"
                  value={formData.arrivalFrom}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Terminal"
                  name="terminal"
                  value={formData.terminal}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Door Number"
                  name="doorNo"
                  value={formData.doorNo}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Additional Services */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Services</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="childSeat"
                    checked={formData.childSeat === 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">Child Seat Required</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="disableSeat"
                    checked={formData.disableSeat === 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">Disabled Access Required</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="returnTrip"
                    checked={formData.returnTrip === 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">Return Trip</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="meetGreet"
                    checked={formData.meetGreet === 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">Meet & Greet Service</label>
                </div>
              </div>
            </div>

            {/* Driver Assignment */}
            {isEditMode && (
              <div className="col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Driver Assignment</h3>
                {assignMessage && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
                    {assignMessage}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Assign Driver"
                    name="driverId"
                    value={formData.driverId}
                    onChange={handleSelectChange("driverId")}
                    options={[
                      { value: "", label: "Select a driver" },
                      ...driverOptions,
                    ]}
                  />
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAssignDriver}
                      disabled={assignLoading || !formData.driverId}
                      className="w-full md:w-auto px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {assignLoading ? "Assigning..." : "Assign & notify customer"}
                    </button>
                  </div>
                </div>
                {assignedDriverName && (
                  <p className="mt-3 text-sm text-gray-600">
                    Currently assigned:{" "}
                    <span className="font-medium text-gray-900">
                      {assignedDriverName}
                      {assignedDriverPhone ? ` · ${assignedDriverPhone}` : ""}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Payment and Status */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment and Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Payment Mode"
                  name="paymentMode"
                  value={formData.paymentMode}
                  onChange={handleSelectChange("paymentMode")}
                  options={paymentModeOptions}
                  required
                />
                <InputWithLabel
                  label="Amount"
                  type="number"
                  name="amount"
                  value={formatFormNumber(formData.amount)}
                  onChange={handleInputChange}
                  required
                />
                <Select
                  label="Booking Status"
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange("status")}
                  options={statusOptions}
                  required
                />
                <Select
                  label="Payment Status"
                  name="paymentStatus"
                  value={formData.paymentStatus}
                  onChange={handleSelectChange("paymentStatus")}
                  options={paymentStatusOptions}
                  required
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                placeholder="Enter any additional information or special requirements..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/bookings")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : isEditMode ? "Update Booking" : "Create Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
